import { useEffect, useRef, useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import TodoForm from "./TodoForm";

interface Todo {
  id: string;
  body: string;
  completed: boolean;
  created_at: string;
}

// Raw API shape: gorm.Model fields (ID, CreatedAt) stay PascalCase; body/completed are lowercase from JSON tags
interface ArchivedTodo {
  ID: number;
  body: string;
  completed: string;
  CreatedAt: string;
}

const TodoList = ({ onUnauthorized }: { onUnauthorized: () => void }) => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTodoId, setEditingTodoId] = useState<string | null>(null); // Track which todo is being edited
  const [editedBody, setEditedBody] = useState<string>(""); // Track the edited body text
  const [fadingTodos, setFadingTodos] = useState<Set<string>>(new Set()); // Track todos being removed
  const [activeTab, setActiveTab] = useState<'active' | 'completed' | 'archived'>('active');
  const [deleteConfirmTodo, setDeleteConfirmTodo] = useState<Todo | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [archivedTodos, setArchivedTodos] = useState<ArchivedTodo[]>([]);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [archivedOffset, setArchivedOffset] = useState(0);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const archivedFetchedRef = useRef(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 768px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const fetchArchived = async (offset: number) => {
    archivedFetchedRef.current = true;
    setArchivedLoading(true);
    try {
      const response = await fetch(`/api/todos/archived?limit=20&offset=${offset}`);
      if (response.status === 401) { onUnauthorized(); return; }
      if (!response.ok) throw new Error('Failed to fetch archived todos');
      const data = await response.json();
      setArchivedTodos(prev => offset === 0 ? data.todos : [...prev, ...data.todos]);
      setArchivedTotal(data.total);
      setArchivedOffset(offset + data.todos.length);
    } catch (err) {
      console.error('Error fetching archived todos:', err);
    } finally {
      setArchivedLoading(false);
    }
  };

  const activeTodos = todos.filter(t => !t.completed);
  const completedTodos = todos.filter(t => t.completed);

  // Fetch todos on page load
  useEffect(() => {
    const fetchTodos = async () => {
      try {
        const response = await fetch("/api/todos");
        if (response.status === 401) { onUnauthorized(); return; }
        if (!response.ok) {
          throw new Error("Failed to fetch todos");
        }

        const data = await response.json();

        const todos = data.todos
          .map((todo: any) => ({
            id: todo.ID.toString(),
            body: todo.body,
            completed: !!todo.completed, // null → false, timestamp string → true
            created_at: todo.CreatedAt,
          }));

        setTodos(todos);
      } catch (error) {
        setError("Error fetching todos");
        console.error("Error fetching todos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTodos();
  }, []);

  const addTodo = async (body: string) => {
    try {
      const response = await fetch("/api/todos", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ body }),
      });

      if (response.status === 401) { onUnauthorized(); return; }
      if (!response.ok) {
        throw new Error("Failed to add todo");
      }

      const newTodo = await response.json();

      const formattedTodo: Todo = {
        id: newTodo.ID.toString(),
        body: newTodo.body,
        completed: !!newTodo.completed, // null → false, timestamp string → true
        created_at: newTodo.CreatedAt,
      };

      setTodos((prev) => [formattedTodo, ...prev]); // Add the new todo to the list
    } catch (error) {
      console.error("Error adding todo:", error);
    }
  };

  const toggleTodoCompletion = async (id: string, completed: boolean) => {
    try {
      const response = await fetch(`/api/todos/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ completed }),
      });

      if (response.status === 401) { onUnauthorized(); return; }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to update todo: ${response.status}, ${errorText}`
        );
      }

      // Clear edit state if this todo was being edited
      if (completed && editingTodoId === id) {
        setEditingTodoId(null);
        setEditedBody('');
      }

      if (completed) {
        // Add the todo to the fading list
        setFadingTodos((prev) => new Set(prev).add(id));

        // Wait for the fade-out animation to complete, then flip completed=true
        setTimeout(() => {
          setTodos((prevTodos) =>
            prevTodos.map((todo) => todo.id === id ? { ...todo, completed: true } : todo)
          );
          setFadingTodos((prev) => {
            const updated = new Set(prev);
            updated.delete(id);
            return updated;
          });
        }, 500); // Match the animation duration in CSS
      } else {
        // If unmarking as completed, just update the state
        setTodos((prevTodos) =>
          prevTodos.map((todo) =>
            todo.id === id ? { ...todo, completed } : todo
          )
        );
      }
    } catch (error) {
      console.error("Error updating todo:", error);
    }
  };

  const updateTodoBody = async (id: string, body: string) => {
    try {
      const response = await fetch(`/api/todos/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ body }),
      });

      if (response.status === 401) { onUnauthorized(); return; }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to update todo: ${response.status}, ${errorText}`
        );
      }

      // Update the todo in the state
      setTodos((prevTodos) =>
        prevTodos.map((todo) => (todo.id === id ? { ...todo, body } : todo))
      );

      setEditingTodoId(null); // Exit edit mode
    } catch (error) {
      console.error("Error updating todo:", error);
    }
  };

  const handleTouchStart = (todo: Todo) => {
    if (editingTodoId === todo.id) return;
    longPressTimer.current = setTimeout(() => setDeleteConfirmTodo(todo), 600);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const deleteTodo = async (id: string) => {
    try {
      const response = await fetch(`/api/todos/${id}`, { method: 'DELETE' });
      if (response.status === 401) { onUnauthorized(); return; }
      if (!response.ok) throw new Error('Failed to delete todo');
      setTodos(prev => prev.filter(t => t.id !== id));
      setDeleteConfirmTodo(null);
    } catch (err) {
      console.error('Error deleting todo:', err);
    }
  };

  if (loading) {
    return <p className='text-center text-gray-500'>Loading todos...</p>;
  }

  if (error) {
    return <p className='text-center text-red-500'>{error}</p>;
  }

  return (
    <div className='max-w-4xl mx-auto p-6'>
      {deleteConfirmTodo && (
        <div
          className='fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4 pb-8'
          onClick={() => setDeleteConfirmTodo(null)}
        >
          <div
            className='bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl'
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className='text-lg font-semibold text-gray-900 mb-1'>Delete todo?</h3>
            <p className='text-gray-500 text-sm mb-6 line-clamp-2'>"{deleteConfirmTodo.body}"</p>
            <div className='flex gap-3'>
              <button
                onClick={() => setDeleteConfirmTodo(null)}
                className='flex-1 px-4 py-3 rounded-xl bg-gray-100 text-gray-700 font-semibold text-base'
              >
                Cancel
              </button>
              <button
                onClick={() => deleteTodo(deleteConfirmTodo.id)}
                className='flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-semibold text-base'
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <h1 className='header-font text-6xl font-bold text-gray-900 mb-8'>Lazy Fuck</h1>

      {activeTab === 'active' && <TodoForm onAddTodo={addTodo} />}

      <div className='flex gap-2 mb-6'>
        <button
          onClick={() => setActiveTab('active')}
          className={activeTab === 'active'
            ? 'px-4 py-1.5 rounded-full bg-blue-600 text-white text-sm font-semibold'
            : 'px-4 py-1.5 rounded-full bg-gray-100 text-gray-500 text-sm font-semibold hover:bg-gray-200'
          }
        >
          Active ({activeTodos.length})
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={activeTab === 'completed'
            ? 'px-4 py-1.5 rounded-full bg-blue-600 text-white text-sm font-semibold'
            : 'px-4 py-1.5 rounded-full bg-gray-100 text-gray-500 text-sm font-semibold hover:bg-gray-200'
          }
        >
          Completed ({completedTodos.length})
        </button>
        {/* Archived tab — desktop only */}
        {isDesktop && (
          <button
            onClick={() => {
              setActiveTab('archived');
              if (!archivedFetchedRef.current) fetchArchived(0);
            }}
            className={activeTab === 'archived'
              ? 'px-4 py-1.5 rounded-full bg-blue-600 text-white text-sm font-semibold'
              : 'px-4 py-1.5 rounded-full bg-gray-100 text-gray-500 text-sm font-semibold hover:bg-gray-200'
            }
          >
            Archived
          </button>
        )}
      </div>

      <div className='space-y-6'>
        {activeTab === 'active' && (
          <>
            {activeTodos.length === 0 && (
              <p className='text-center text-gray-400'>Nothing to do — enjoy the moment.</p>
            )}
            {activeTodos.map((todo) => (
              <article
                key={todo.id}
                className={`border-b border-gray-200 pb-6 select-none ${
                  fadingTodos.has(todo.id)
                    ? 'opacity-0 transition-opacity duration-500'
                    : ''
                }`}
                onTouchStart={() => handleTouchStart(todo)}
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchEnd}
                onContextMenu={(e) => e.preventDefault()}
              >
                <div className='flex items-center justify-between'>
                  <div>
                    {editingTodoId === todo.id ? (
                      <input
                        type='text'
                        value={editedBody}
                        onChange={(e) => setEditedBody(e.target.value)}
                        onBlur={() => updateTodoBody(todo.id, editedBody)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') updateTodoBody(todo.id, editedBody);
                          else if (e.key === 'Escape') setEditingTodoId(null);
                        }}
                        className='border border-gray-300 rounded px-2 py-1'
                      />
                    ) : (
                      <h2
                        className='text-2xl font-semibold text-gray-900 mb-2 cursor-pointer text-left'
                        onClick={() => {
                          setEditingTodoId(todo.id);
                          setEditedBody(todo.body);
                        }}
                      >
                        {todo.body}
                      </h2>
                    )}
                    <time className='block text-left text-sm text-gray-500'>
                      {formatDistanceToNow(parseISO(todo.created_at), { addSuffix: true })}
                    </time>
                  </div>

                  <button
                    aria-label={`Mark "${todo.body}" as complete`}
                    disabled={fadingTodos.has(todo.id)}
                    onClick={() => toggleTodoCompletion(todo.id, true)}
                    className={`w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0 ${
                      fadingTodos.has(todo.id) ? 'cursor-not-allowed opacity-50' : 'hover:border-blue-400'
                    }`}
                  />
                </div>
              </article>
            ))}
          </>
        )}
        {activeTab === 'completed' && (
          <>
            {completedTodos.length === 0 && (
              <p className='text-center text-gray-400'>Nothing completed yet.</p>
            )}
            {completedTodos.map((todo) => (
              <article
                key={todo.id}
                className='border-b border-gray-200 pb-6 select-none'
                onTouchStart={() => handleTouchStart(todo)}
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchEnd}
                onContextMenu={(e) => e.preventDefault()}
              >
                <div className='flex items-center justify-between'>
                  <div>
                    <h2 className='text-2xl font-semibold text-gray-400 mb-2 line-through text-left'>
                      {todo.body}
                    </h2>
                    <time className='block text-left text-sm text-gray-400'>
                      {formatDistanceToNow(parseISO(todo.created_at), { addSuffix: true })}
                    </time>
                  </div>

                  <button
                    aria-label={`Mark "${todo.body}" as active`}
                    onClick={() => toggleTodoCompletion(todo.id, false)}
                    className='w-6 h-6 rounded-full bg-blue-600 flex-shrink-0 flex items-center justify-center hover:bg-blue-700'
                  >
                    <svg
                      className='w-3.5 h-3.5 text-white'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={3}
                    >
                      <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                    </svg>
                  </button>
                </div>
              </article>
            ))}
          </>
        )}
        {activeTab === 'archived' && (
          <>
            {archivedLoading && archivedTodos.length === 0 && (
              <p className='text-center text-gray-400'>Loading archived todos...</p>
            )}
            {!archivedLoading && archivedTodos.length === 0 && (
              <p className='text-center text-gray-400'>No archived todos.</p>
            )}
            {archivedTodos.map((todo) => (
              <article key={todo.ID} className='border-b border-gray-200 pb-6'>
                <div className='flex items-center justify-between'>
                  <div>
                    <h2 className='text-2xl font-semibold text-gray-400 mb-2 line-through text-left'>
                      {todo.body}
                    </h2>
                    <time className='block text-left text-sm text-gray-400'>
                      {formatDistanceToNow(parseISO(todo.CreatedAt), { addSuffix: true })}
                    </time>
                  </div>
                </div>
              </article>
            ))}
            {archivedTodos.length < archivedTotal && (
              <button
                onClick={() => fetchArchived(archivedOffset)}
                disabled={archivedLoading}
                className='mt-4 px-4 py-2 rounded bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 disabled:opacity-50'
              >
                {archivedLoading ? 'Loading...' : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TodoList;
